/**
 * Stytch Magic Link Authentication Hooks
 *
 * These hooks provide convenient wrappers for magic link authentication
 * operations using the Stytch B2B headless client.
 */
import { getStytchClient } from './../client/headless-client';
/**
 * Hook to send discovery magic link email
 */
export function useSendDiscoveryEmail() {
    const stytch = getStytchClient();
    return async (email, options) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.magicLinks.email.discovery.send({
                email_address: email,
                discovery_redirect_url: options?.discoveryRedirectUrl || `${window.location.origin}/auth/callback`,
                login_template_id: options?.login_template_id || 'custom_login',
            });
            return {
                success: true,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to send discovery email',
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate discovery magic link
 */
export function useAuthenticateDiscovery() {
    const stytch = getStytchClient();
    return async (token) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.magicLinks.discovery.authenticate({
                discovery_magic_links_token: token,
            });
            return {
                success: true,
                intermediate_session_token: result.intermediate_session_token,
                email_address: result.email_address,
                discovered_organizations: result.discovered_organizations,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate discovery',
                data: error,
            };
        }
    };
}
/**
 * Hook to authenticate regular magic link
 */
export function useAuthenticateMagicLink() {
    const stytch = getStytchClient();
    return async (token, sessionDurationMinutes = 5000) => {
        try {
            if (!stytch) {
                return {
                    success: false,
                    error: 'Stytch client not ready',
                    data: null,
                };
            }
            const result = await stytch.magicLinks.authenticate({
                magic_links_token: token,
                session_duration_minutes: sessionDurationMinutes,
            });
            return {
                success: true,
                member: result.member,
                memberId: result.member?.member_id,
                memberSession: result.member_session,
                sessionToken: result.session_token,
                sessionJwt: result.session_jwt,
                organization: result.organization,
                data: result,
            };
        }
        catch (error) {
            const err = error;
            return {
                success: false,
                error: err.error_message || err.message || 'Failed to authenticate magic link',
                data: error,
            };
        }
    };
}
